import { gql, MutationResult, useMutation } from '@apollo/client';
import { useCallback } from 'react';
import { MAYA_GRAPHQL_ENDPOINT } from 'basics/constants/common.constants';
import { MutationReturnType } from 'basics/types/maya.types';
import { CustomerEngagementPlanning } from 'generated/maya.types';
import { extractApolloErrorDetails } from 'services/graphql/extractApolloErrorDetails';
import Logger from 'services/logger';

export const updateCepTargetsSyncInfoMutationCall = gql`
  mutation updateCepTargetsSyncInfo($id: String!, $lastSyncTargetCount: Int!) {
    updateCepTargetsSyncInfo(id: $id, lastSyncTargetCount: $lastSyncTargetCount) {
      _id
      lastTargetsSyncAt
      lastSyncTargetCount
    }
  }
`;

type ResultType = {
  updateCepTargetsSyncInfo: CustomerEngagementPlanning;
};

type UseUpdateCepTargetsSyncInfoMutation = [
  (id: string, lastSyncTargetCount: number) => Promise<MutationReturnType<CustomerEngagementPlanning | undefined>>,
  MutationResult<ResultType>,
];

export const useUpdateCepTargetsSyncInfoMutation = (): UseUpdateCepTargetsSyncInfoMutation => {
  const [updateCepTargetsSyncInfo, state] = useMutation<ResultType>(updateCepTargetsSyncInfoMutationCall, {
    fetchPolicy: 'network-only',
    context: { clientName: MAYA_GRAPHQL_ENDPOINT },
  });

  const executeMutation = useCallback(
    async (id: string, lastSyncTargetCount: number) => {
      try {
        const { data } = await updateCepTargetsSyncInfo({
          variables: {
            id,
            lastSyncTargetCount,
          },
        });

        return { data: data?.updateCepTargetsSyncInfo, error: null };
      } catch (error: unknown) {
        if (error instanceof Error) {
          const customContext = extractApolloErrorDetails(error);
          Logger.logError({
            error,
            customContext,
            transactionName: 'updateCepTargetsSyncInfo.ts - useUpdateCepTargetsSyncInfoMutation',
          });

          return { data: null, error: customContext.message as string };
        }

        return { data: null, error: null };
      }
    },
    [updateCepTargetsSyncInfo],
  );

  return [executeMutation, state];
};
